import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { GoogleMap, GoogleMapsModule } from '@angular/google-maps';
import {
  Appointment,
  CountryBounds,
  DotFitter,
  Fitter,
  GeoCoordinates,
  MapType,
} from '@sc/data-access-models';

import { NgClass, NgFor, NgIf } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { ConfigurationService } from '@sc/data-access-configuration';
import {
  FastAccessTabMenuComponent,
  IconComponent,
  SpinnerComponent,
} from '@sc/ui-components';
import { BehaviorSubject, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  MapOnBoundsChangeEvent,
  MapOnCenterChangeEvent,
  MapOnZoomChangeEvent,
} from '../../types/map';
import { coordsEqual, latLngToGeoCoordinates } from '../../utils/geo';
import { default as mapStyles } from './assets/styles.json';
import { default as mapStylesWithPOI } from './assets/stylesWithPOI.json';
import { GmapAppointmentTimeComponent } from './components/gmap-appointment-time/gmap-appointment-time.component';
import { GmapDotFittersComponent } from './components/gmap-dot-fitters/gmap-dot-fitters.component';
import { GmapFitterCardComponent } from './components/gmap-fitter-card/gmap-fitter-card.component';
import { GmapFittersMarkComponent } from './components/gmap-fitters-mark/gmap-fitters-mark.component';

const fallbackLat = 48.85756;
const fallbackLon = 2.3428;

@Component({
  selector: 'sc-gmap',
  styleUrls: ['./gmap.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgIf,
    SpinnerComponent,
    FastAccessTabMenuComponent,
    GoogleMapsModule,
    NgClass,
    GmapAppointmentTimeComponent,
    GmapFittersMarkComponent,
    NgFor,
    GmapFitterCardComponent,
    GmapDotFittersComponent,
    IconComponent,
    TranslateModule,
  ],
  template: `
    <div
      class="loader"
      *ngIf="useLoader && loading"
      fxLayoutAlign="center center"
    >
      <sc-spinner></sc-spinner>
    </div>

    <sc-fast-access-tab-menu
      [linkList]="[
        {
          id: 'before-map-access-menu',
          target: 'after-map-access-menu',
          textKey: 'Map.fastAccessMenu.beforeMap'
        }
      ]"
    ></sc-fast-access-tab-menu>

    <div *ngIf="!loading" class="gmap-container">
      <google-map
        [width]="mapWidth"
        [height]="mapHeight"
        [options]="gmapOptions"
        [center]="{
          lat: centerLatitude,
          lng: centerLongitude
        }"
        [zoom]="this.defaultZoomLevel"
        (boundsChanged)="onBoundsChange()"
        (zoomChanged)="onZoomChange()"
        (idle)="onIdle()"
        (tilesloaded)="onMapReady()"
        (mouseenter)="toggleHoverMap(true)"
        (mouseleave)="toggleHoverMap(false)"
      >
        <div
          class="on-scroll-hint"
          [ngClass]="{
            'show-scroll-hint':
              showScrollHint && currentMapType === 'DISPLAY_APPOINTMENTS_TIME'
          }"
        >
          {{ 'Map.scrollHint' | translate }}
        </div>

        <!-- Display for search page -->
        <div *ngIf="currentMapType === 'DISPLAY_APPOINTMENTS_TIME'">
          <sc-gmap-appointment-time
            *ngIf="firstOutsideAppointment"
            [map]="googleMap?.googleMap"
            [appointment]="firstOutsideAppointment"
            [fitter]="getFitterFromId(firstOutsideAppointment.fitterId)"
            [fitterHover]="fitterHover"
            [location]="locationPin"
            (clickEvent)="onAppointmentClickEvent(firstOutsideAppointment)"
          ></sc-gmap-appointment-time>

          <sc-gmap-fitters-mark
            [map]="googleMap?.googleMap"
            fitterIcon="map_location"
            [location]="locationPin"
          ></sc-gmap-fitters-mark>

          <div *ngFor="let appointment of firstAppointments">
            <sc-gmap-appointment-time
              *ngIf="appointment"
              [map]="googleMap?.googleMap"
              [appointment]="appointment"
              [fitter]="getFitterFromId(appointment.fitterId)"
              [fitterHover]="fitterHover"
              [location]="getCoordinatesFromAppointment(appointment)"
              (clickEvent)="onAppointmentClickEvent(appointment)"
            ></sc-gmap-appointment-time>
          </div>

          <div *ngFor="let dotFitter of dotFitters">
            <sc-gmap-fitter-card
              *ngIf="isFitterWithCard(dotFitter.id)"
              [map]="googleMap?.googleMap"
              [fitter]="getFitterFromId(dotFitter.id)"
              [fitterHover]="fitterHover"
              [location]="dotFitter.location"
              (clickEvent)="onFitterCardClickEvent($event)"
            >
            </sc-gmap-fitter-card>

            <sc-gmap-dot-fitters
              *ngIf="dotFitter"
              [map]="googleMap?.googleMap"
              [dotFitter]="dotFitter"
              [onError]="isOnError(dotFitter)"
              [fitter]="getFitterFromId(dotFitter.id)"
              [fitterHover]="fitterHover"
              (fitterClickEvent)="onDotFitterClickEvent($event)"
            ></sc-gmap-dot-fitters>
          </div>
        </div>

        <!-- Display for fitter page -->
        <div *ngIf="currentMapType === 'DISPLAY_FITTER'">
          <sc-gmap-fitters-mark
            [map]="googleMap?.googleMap"
            fitterIcon="fitter"
            [location]="locationPin"
          ></sc-gmap-fitters-mark>
        </div>

        <!-- Other Display (currently not used) -->
        <div *ngIf="currentMapType === 'DISPLAY_FITTERS_MARK'">
          <sc-gmap-fitters-mark
            [map]="googleMap?.googleMap"
            fitterIcon="map_location"
            [location]="locationPin"
          ></sc-gmap-fitters-mark>
        </div>

        <button
          #fitterItineraryBtn
          class="fitter-itinerary-btn"
          [style.visibility]="itineraryBtnVisibility"
          (click)="onFitterTrajectButtonClick()"
          *ngIf="currentMapType === 'DISPLAY_FITTER'"
        >
          <sc-icon name="itinerary"></sc-icon>
          <span>{{ 'Map.itineraryButton' | translate }}</span>
        </button>
      </google-map>
    </div>

    <sc-fast-access-tab-menu
      [linkList]="[
        {
          id: 'after-map-access-menu',
          target: 'before-map-access-menu',
          textKey: 'Map.fastAccessMenu.afterMap'
        }
      ]"
    ></sc-fast-access-tab-menu>
  `,
})
export class GmapComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild(GoogleMap, { static: false }) googleMap: GoogleMap;
  @ViewChild('fitterItineraryBtn', { static: false, read: ElementRef })
  itineraryBtn: ElementRef;

  @Input() public loading: boolean;
  @Input() public useLoader = true;

  @Input() public mapWidth = '100%';
  @Input() public mapHeight = '100%';
  @Input() public defaultLatitude = fallbackLat;
  @Input() public defaultLongitude = fallbackLon;
  @Input() public defaultZoomLevel = 12;
  @Input() public defaultMode = 'light';
  @Input() public options: Partial<google.maps.MapOptions> = {};
  @Input() public locationPin: GeoCoordinates;
  @Input() public showPOI = false;
  @Input() public onMapChange = false;
  @Input() public countryViewBounds: CountryBounds;

  @Input() public currentMapType: MapType = 'DISPLAY_APPOINTMENTS_TIME';
  @Input() public firstOutsideAppointment: Appointment = null;
  @Input() public firstAppointments: Appointment[] = [];
  @Input() public fitters: Fitter[];
  @Input() public dotFitters: DotFitter[];
  @Input() public fittersWithCard: Fitter[] = [];
  @Input() public errorDotFitters: DotFitter[];
  @Input() public fitterHover: string;

  @Output()
  appointmentClickEvent: EventEmitter<Appointment> = new EventEmitter<Appointment>();
  @Output() fitterCardClickEvent = new EventEmitter<Fitter>();
  @Output() fitterTrajectEvent = new EventEmitter<void>();
  @Output() zoomChange = new EventEmitter<MapOnZoomChangeEvent>();
  @Output() centerChange = new EventEmitter<MapOnCenterChangeEvent>();
  @Output() boundsChange = new EventEmitter<MapOnBoundsChangeEvent>();
  @Output()
  dotFitterClickEvent: EventEmitter<Fitter> = new EventEmitter<Fitter>();

  mapIsLoaded: boolean;
  isMouseOnMap = false;
  showScrollHint: boolean = null;
  lastScroll = Date.now();

  gmapOptions: google.maps.MapOptions = {
    styles: this.getMapStyleKey(),
    gestureHandling: 'cooperative',
    scrollwheel: false,
    disableDefaultUI: true,
    streetViewControl: false,
  };

  itineraryBtnVisibility: 'visible' | 'hidden' = 'hidden';

  centerLatitude = fallbackLat;
  centerLongitude = fallbackLon;
  bounds: google.maps.LatLngBounds;

  displayDotFitters = new BehaviorSubject<DotFitter[]>(null);

  lastAutoBounds: google.maps.LatLngBounds = null;
  lastAutoCenter: google.maps.LatLng = null;
  lastAutoZoom: number = null;

  resizeOnNewDotsSub = new Subscription();

  constructor(
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly configService: ConfigurationService
  ) {}

  ngOnInit(): void {
    this.centerLatitude = this.defaultLatitude;
    this.centerLongitude = this.defaultLongitude;
    if (!this.locationPin) {
      this.locationPin = {
        lat: this.defaultLatitude,
        lon: this.defaultLongitude,
      };
    }

    this.assembleOptions();

    if (this.dotFitters?.length) {
      this.displayDotFitters.next(
        this.dotFitters.filter((dotF) => dotF.displayAppointments)
      );
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.googleMap) {
      if (
        (changes.defaultLatitude || changes.defaultLongitude) &&
        changes?.defaultLatitude?.currentValue !== null &&
        changes?.defaultLongitude?.currentValue
      ) {
        this.defaultLatitude = changes?.defaultLatitude?.currentValue as number;
        this.defaultLongitude = changes?.defaultLongitude
          ?.currentValue as number;
        if (
          this.defaultLatitude !== this.centerLatitude ||
          this.defaultLongitude !== this.centerLongitude
        ) {
          this.centerLatitude = this.defaultLatitude;
          this.centerLongitude = this.defaultLongitude;
        }
      }
      if (changes.dotFitters) {
        this.dotFitters = changes.dotFitters.currentValue;
        this.displayDotFitters.next(
          this.dotFitters.filter((dotF) => dotF.displayAppointments)
        );
      }
    }
  }

  ngOnDestroy(): void {
    this.resizeOnNewDotsSub.unsubscribe();
  }

  toggleHoverMap(isOnMap: boolean) {
    this.isMouseOnMap = isOnMap;
  }

  @HostListener('window:scroll')
  onScroll() {
    if (this.isMouseOnMap) {
      this.showScrollHint = true;
      this.lastScroll = Date.now();

      setTimeout(() => {
        if (Date.now() - this.lastScroll > 1500) {
          this.showScrollHint = false;
          this.changeDetectorRef.detectChanges();
        }
      }, 1600);
    }
  }

  isOnError(df: DotFitter) {
    return (
      this.errorDotFitters?.findIndex(
        (errordf) => errordf.id === df.id && errordf.brandId === df.brandId
      ) > -1
    );
  }

  assembleOptions() {
    this.gmapOptions = {
      styles: this.getMapStyleKey(),
      gestureHandling: 'cooperative',
      scrollwheel: false,
      disableDefaultUI: true,
      streetViewControl: false,
      ...this.fitterMapOptions(),
      ...this.apmtOrFitterMarkMapOptions(),
      ...this.options,
    };
  }

  fitterMapOptions(): Partial<google.maps.MapOptions> {
    if (this.currentMapType === 'DISPLAY_FITTER') {
      return {
        zoomControl: false,
      };
    } else {
      return {};
    }
  }

  apmtOrFitterMarkMapOptions(): Partial<google.maps.MapOptions> {
    if (
      this.currentMapType === 'DISPLAY_APPOINTMENTS_TIME' ||
      this.currentMapType === 'DISPLAY_FITTERS_MARK'
    ) {
      return {
        maxZoom: 18,
        minZoom: this.countryViewBounds ? undefined : 6,
        scrollwheel: null,
        zoomControl: true,
        zoomControlOptions: {
          position: google.maps.ControlPosition.BOTTOM_LEFT,
        },
        restriction: this.countryViewBounds
          ? {
              latLngBounds: this.countryViewBounds,
              strictBounds: false,
            }
          : undefined,
      };
    } else {
      return {};
    }
  }

  getMapStyleKey() {
    switch (this.defaultMode) {
      case 'dark':
        if (!this.showPOI) {
          return mapStyles.dark;
        } else {
          return mapStylesWithPOI.dark;
        }
      default:
        if (!this.showPOI) {
          return mapStyles.light;
        } else {
          return mapStylesWithPOI.light;
        }
    }
  }

  getCoordinatesFromAppointment(appointment: Appointment) {
    const result = this.fitters?.find(
      (fitter) => fitter.id === appointment?.fitterId
    );

    if (result) {
      return {
        lat: result.location.lat,
        lon: result.location.lon,
      };
    }
    return { lat: 0, lon: 0 };
  }

  getFitterFromId(fitterId: string) {
    return this.fitters?.find((f) => f.id === fitterId);
  }

  isFitterWithCard(fitterId: string) {
    return (
      !!this.getFitterFromId(fitterId) &&
      this.fittersWithCard.some((f) => f.id === fitterId)
    );
  }

  onMapReady() {
    if (!this.mapIsLoaded) {
      this.mapIsLoaded = true;

      if (this.itineraryBtn) {
        this.googleMap.controls[google.maps.ControlPosition.BOTTOM_RIGHT].push(
          this.itineraryBtn.nativeElement
        );
      }

      if (
        (this.centerLatitude !== this.defaultLatitude ||
          this.centerLongitude !== this.defaultLongitude) &&
        this.defaultLatitude &&
        this.defaultLongitude
      ) {
        this.detectCenterChange({
          lat: this.defaultLatitude,
          lon: this.defaultLongitude,
        });
      }

      this.resizeOnNewDotsSub.unsubscribe();
      this.resizeOnNewDotsSub = this.displayDotFitters
        .pipe(
          map((dotFitters) => {
            // Do not use no-slot fitters on resize if there are fitters with slots
            const hasFittersWithSlots = dotFitters?.some(
              (dot) =>
                this.configService.getBrandSearchVersion(dot.brandId) !==
                'NO_SLOT'
            );

            if (hasFittersWithSlots) {
              return dotFitters.filter(
                (dot) =>
                  this.configService.getBrandSearchVersion(dot.brandId) !==
                  'NO_SLOT'
              );
            }

            return dotFitters;
          })
        )
        .subscribe((dotfitter) => {
          if (dotfitter && dotfitter.length) {
            if (!this.onMapChange) {
              const bounds = new google.maps.LatLngBounds();
              // add search location pin to the bounds
              bounds.extend(
                new google.maps.LatLng(
                  this.locationPin.lat,
                  this.locationPin.lon
                )
              );
              // add each dotFitter to the bounds
              dotfitter.forEach((df) => {
                bounds.extend(
                  new google.maps.LatLng(df.location.lat, df.location.lon)
                );
              });

              this.googleMap?.fitBounds(bounds, 23);
              this.fitBounds(bounds, 23);
            }
          }
        });
    }
  }

  fitBounds(bounds: google.maps.LatLngBounds, padding: number) {
    const previousBounds = this.googleMap?.getBounds();
    const previousCenter = this.googleMap?.getCenter().toString();
    const previousZoom = this.googleMap?.getZoom();

    this.googleMap?.fitBounds(bounds, padding);

    const nextBounds = this.googleMap?.getBounds();
    if (!previousBounds.equals(nextBounds)) {
      this.lastAutoBounds = nextBounds;
    }

    const nextCenter = this.googleMap?.getCenter();
    if (previousCenter !== nextCenter.toString()) {
      this.lastAutoCenter = nextCenter;
    }

    const nextZoom = this.googleMap?.getZoom();
    if (previousZoom !== nextZoom) {
      this.lastAutoZoom = nextZoom;
    }
  }

  detectCenterChange(coordinates: GeoCoordinates) {
    if (
      this.centerLatitude !== coordinates.lat ||
      this.centerLongitude !== coordinates.lon
    ) {
      this.centerLatitude = coordinates.lat;
      this.centerLongitude = coordinates.lon;
      this.changeDetectorRef.detectChanges();

      let isAutoCenter = false;
      if (
        this.lastAutoCenter &&
        coordsEqual(latLngToGeoCoordinates(this.lastAutoCenter), coordinates)
      ) {
        isAutoCenter = true;
      } else {
        this.lastAutoCenter = null;
      }

      this.centerChange.emit({
        center: coordinates,
        isAuto: isAutoCenter,
      });
    }
  }

  onBoundsChange() {
    this.bounds = this.googleMap?.getBounds();

    let isAutoBounds = false;
    if (this.lastAutoBounds && this.lastAutoBounds.equals(this.bounds)) {
      isAutoBounds = true;
    } else {
      this.lastAutoBounds = null;
    }

    this.boundsChange.emit({ bounds: this.bounds, isAuto: isAutoBounds });
  }

  onZoomChange() {
    const newZoom = this.googleMap?.getZoom();

    let isAutoZoom = false;
    if (this.lastAutoZoom === newZoom) {
      isAutoZoom = true;
    } else {
      this.lastAutoZoom = null;
    }

    this.zoomChange.emit({
      zoom: newZoom,
      isAuto: isAutoZoom,
    });
  }

  onIdle() {
    const pos = this.googleMap?.getCenter();
    this.detectCenterChange({ lat: pos.lat(), lon: pos.lng() });
  }

  onAppointmentClickEvent(appointment: Appointment): void {
    this.appointmentClickEvent.emit(appointment);
  }

  onFitterCardClickEvent(fitter: Fitter) {
    this.fitterCardClickEvent.emit(fitter);
  }

  onDotFitterClickEvent(fitter: Fitter): void {
    this.dotFitterClickEvent.emit(fitter);
  }

  onFitterTrajectButtonClick(): void {
    this.fitterTrajectEvent.emit();
  }
}
